import { useEffect, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

/** App-wide loading indicator. Renders a thin animated bar at the very top of
 *  the viewport while any tanstack-query fetch or mutation is in flight, so the
 *  user gets feedback during longer requests (e.g. agent sync calls that take
 *  several seconds).
 *
 *  - Listens to react-query's global counters via the public hooks — no
 *    per-call wiring needed; every `useQuery` / `useMutation` participates.
 *  - 250ms show-delay avoids flicker for fast cached responses.
 *  - Hides immediately on completion so the UI doesn't feel laggy.
 *  - `pointer-events-none` + fixed position so it never blocks input. */
const SHOW_DELAY_MS = 250;

export function GlobalLoadingBar() {
  const isMutating = useIsMutating();
  const isFetching = useIsFetching();
  const active = isMutating > 0 || isFetching > 0;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    const id = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [active]);

  return (
    <div
      role="status"
      aria-label="Loading"
      aria-hidden={!visible}
      className={`pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5 overflow-hidden transition-opacity duration-150 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="progress-bar h-full bg-primary" />
    </div>
  );
}
